import { useContext, useEffect, useState } from 'react';
import { ButtonBack } from '../../../common/Button/ButtonBack';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import TextInput from '../../../common/TextInput/TextInput';
import DateInput from '../../../common/DateInput/DateInput';
import { SiteContext } from '../../../../context/SiteContext';
import SelectInput from '../../../common/SelectInput/SelectInput';
import {
  DealerDashboardItem,
  InsuranceJourneyContext,
  SelectOpt,
  InsuranceJourneyState
} from '../../../common/CommonTypes';
import { ButtonContinue } from '../../../common/Button/ButtonContinue';
import RadioOptions from '../../../common/RadioOptions/RadioOptions';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import { CarInsuranceApi } from '../../../../api/CarInsuranceApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  DefaultRadioOptions,
  PageAbsolutePath
} from '../../../../consts/Common';
import DealerQuoteDialog from '../DealerQuoteDialog';
import useCarInsuranceController from '../../../../hooks/insuranceJourney/useCarInsuranceController';
import { RetrieveQuoteRequestType } from '../../../../api/Models/InsuranceJourney';
import { scrollToTop } from '../../../common/HelperMethods';
import InsuranceDocumentLink from '../InsuranceDocumentLink';
import LoadingContainer from '../../../common/LoadingContainer/LoadingContainer';
import { useErrorBoundary } from 'react-error-boundary';

export type DealerAdminFields = {
  quoteNumber: string;
  customerSurname: string;
  postcode: string;
  showFinanceApplications: string;
  coverNoteStartDate: string | undefined;
  coverNoteEndDate: string | undefined;
  recordsPerPage: string;
};

export type DealerAdminFieldsErrors = {
  quoteNumber: string;
  customerSurname: string;
  postcode: string;
  showFinanceApplications: string;
  coverNoteStartDate: string;
  coverNoteEndDate: string;
  recordsPerPage: string;
};

const DealerAdminPage = () => {
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [includeDriveaway, setIncludeDriveaway] = useState(false);
  const [includeExcessReturn, setIncludeExcessReturn] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [quoteId, setQuoteId] = useState('');
  const [activeVrn, setActiveVrn] = useState('');
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const { site } = useContext(SiteContext);
  const { brand } = site;
  const brandPhoneNumber = brand.brandPhoneNumbers.quotesAndRenewalsNumber;

  const { retrieveQuote } = useCarInsuranceController();

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { dealerHomeOptions } = journey;

  const { commonEvents } = pageEvents;

  const { setQuote } = commonEvents;

  const dealerId = site.user.insuranceDealerLoginUserName;

  const pageRecordOptions: SelectOpt[] = [
    { id: '10', text: '10', value: '10', disabled: false },
    { id: '20', text: '20', value: '20', disabled: false },
    { id: '50', text: '50', value: '50', disabled: false }
  ];
  const chevronRightComponent = <FontAwesomeIcon icon={faChevronRight} />;

  const intialFormValues: DealerAdminFields = {
    quoteNumber: '',
    postcode: '',
    customerSurname: '',
    recordsPerPage: '',
    showFinanceApplications: '',
    coverNoteEndDate: undefined,
    coverNoteStartDate: undefined
  };

  const intialErrors: DealerAdminFieldsErrors = {
    showFinanceApplications: '',
    coverNoteEndDate: '',
    coverNoteStartDate: '',
    customerSurname: '',
    postcode: '',
    quoteNumber: '',
    recordsPerPage: ''
  };

  const [pageFields, setPageFields] =
    useState<DealerAdminFields>(intialFormValues);

  const [pageErrors] = useState<DealerAdminFieldsErrors>(intialErrors);

  const [dashboardResults, setDashboardResults] = useState<
    DealerDashboardItem[]
  >([]);

  const [originalDashboardResults, setOriginalDashboardResults] = useState<
    DealerDashboardItem[]
  >([]);

  const [showPopup, setShowPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState('');
  const [popupMessageParagraph1, setPopupMessageParagraph1] = useState('');
  const [popupMessageParagraph2, setPopupMessageParagraph2] = useState('');

  const onChangeQuote = (e: React.FormEvent<HTMLInputElement>): void => {
    const value = e.currentTarget.value;
    if (CommonValidation.isValidAlphaNumeric(value))
      setPageFields({ ...pageFields, quoteNumber: value });
  };

  const onChangeCustomerSurname = (
    e: React.FormEvent<HTMLInputElement>
  ): void => {
    const value = e.currentTarget.value;
    if (CommonValidation.isValidAlphabetic(value))
      setPageFields({ ...pageFields, customerSurname: value });
  };

  const onChangePostcode = (e: React.FormEvent<HTMLInputElement>): void => {
    const value = e.currentTarget.value;

    if (CommonValidation.isValidAlphaNumeric(value))
      setPageFields({ ...pageFields, postcode: value });
  };

  const onChangeShowFinanceApplications = (value: string): void => {
    setPageFields({ ...pageFields, showFinanceApplications: value });
  };

  const onChangeCoverNoteStartDate = (
    e: React.FormEvent<HTMLInputElement>
  ): void => {
    const value = (e.target as HTMLInputElement).value;
    setPageFields({ ...pageFields, coverNoteStartDate: value });
  };

  const onChangeCoverNoteEndDate = (
    e: React.FormEvent<HTMLInputElement>
  ): void => {
    const value = (e.target as HTMLInputElement).value;
    setPageFields({ ...pageFields, coverNoteEndDate: value });
  };

  const onChangeRecordsPerPage = (value: string): void => {
    setPageFields({ ...pageFields, recordsPerPage: value });
  };

  const getMaxCoverNoteEndDate = (): Date => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  };

  const onContactClick = () => {
    setPopupTitle('CONTACT');
    setPopupMessageParagraph1(
      `Call us on ${brandPhoneNumber}. We can investigate why the application has referred and provide assistance to process the application.`
    );
    setPopupMessageParagraph2('');
    setShowPopup(true);
  };

  const onShowQuoteModal = (
    includeExcessReturn: boolean,
    includeDriveaway: boolean,
    quoteId: string,
    vrn: string
  ) => {
    setIncludeExcessReturn(includeExcessReturn);
    setQuoteId(quoteId);
    setIncludeDriveaway(includeDriveaway);
    setShowQuoteDialog(true);
    setActiveVrn(vrn);
  };

  const onCloseQuoteModal = () => {
    setIncludeExcessReturn(false);
    setIncludeDriveaway(false);
    setQuoteId('');
    setShowQuoteDialog(false);
    setActiveVrn('');
  };

  const onContinueQuoteModal = () => {
    onCloseQuoteModal();
  };

  const onHelpClick = () => {
    setPopupTitle('HELP');
    setPopupMessageParagraph1(
      'To print the insurance certificate, you must ensure your computer settings allow pop - ups.'
    );
    setPopupMessageParagraph2(
      'A policy start date has been entered. The policy will be live from that date.You can still edit the details and print.'
    );
    setShowPopup(true);
  };

  const onCloseModal = () => {
    setShowPopup(false);
  };

  const getFilteredDashboardData = (
    quoteNumber?: string,
    surname?: string,
    postcode?: string,
    coverNoteStartDate?: string,
    coverNoteEndDate?: string
  ): DealerDashboardItem[] => {
    const noteStartDate = !coverNoteStartDate
      ? undefined
      : getDateValue(coverNoteStartDate);
    const noteEndDate = !coverNoteEndDate
      ? undefined
      : getDateValue(coverNoteEndDate);
    const filteredDashboardResult = originalDashboardResults.filter(
      (dashboardItem) => {
        return (
          (!quoteNumber ||
            dashboardItem.quote.toLowerCase() === quoteNumber.toLowerCase()) &&
          (!surname ||
            getCustomerSurname(dashboardItem.customer.toLowerCase()) ===
              surname.toLowerCase()) &&
          (!postcode ||
            dashboardItem.postcode.toLowerCase() === postcode.toLowerCase()) &&
          (!noteStartDate || dashboardItem.startDate >= noteStartDate) &&
          (!noteEndDate || dashboardItem.startDate <= noteEndDate)
        );
      }
    );

    return filteredDashboardResult;
  };

  const getCustomerSurname = (customerName: string) => {
    const nameArray = customerName.split(' ', 2);

    return nameArray[1];
  };

  const getDateValue = (str: string) => {
    const date = new Date(str);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return [day, month, date.getFullYear()].join('/');
  };

  const onSearchClick = () => {
    const filteredResut = getFilteredDashboardData(
      pageFields.quoteNumber,
      pageFields.customerSurname,
      pageFields.postcode,
      pageFields.coverNoteStartDate,
      pageFields.coverNoteEndDate
    );

    setDashboardResults(filteredResut);
  };

  useEffect(() => {
    scrollToTop();
    setLoadingData(true);
    loadDashboardResults();
  }, []);

  useEffect(() => {
    setDashboardResults(dashboardResults);
  }, [dashboardResults]);

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const loadDashboardResults = async () => {
    const controller = new AbortController();

    const onSuccess = (dashboardReport: DealerDashboardItem[]): void => {
      console.log(dashboardReport);
      setDashboardResults(dashboardReport);
      setOriginalDashboardResults(dashboardReport);
      setLoadingData(false);
    };

    await new CarInsuranceApi().GetDealerQuoteListAsync(
      dealerId ?? '',
      site.user.token,
      onSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const retrieveCustomerQuote = (
    customer: string,
    postCode: string,
    emailAddress: string
  ) => {
    const onSuccess = (quote: InsuranceJourneyState): void => {
      setQuote({
        ...quote,
        tiaProductCode: site.brand.tiaProductCode,
        dealerHomeOptions: dealerHomeOptions
      });
      navigate(PageAbsolutePath.Insurance.AboutYou);
    };

    const customerNameArray = customer.split(' ', 2);

    const firstName = customerNameArray[0];
    const lastName = customerNameArray[1];

    const request: RetrieveQuoteRequestType = {
      firstName,
      lastName,
      postCode,
      emailAddress,
      tiaProductCode: site.brand.tiaProductCode
    };

    retrieveQuote(request, site.user.token, onSuccess, onBackendFailure);
  };

  const onClickEdit = (item: DealerDashboardItem) => {
    retrieveCustomerQuote(item.customer, item.postcode, item.email);
  };

  const renderActionBlock = (item: DealerDashboardItem) => {
    switch (item.cnStatusCode) {
      case '1': // Declined
        return renderDeclinedActions(item);
      case '2': // Pending
      case '3': // Pending (eligibile for extension to 5 days)
      case '4': // Pending
        return renderPendingActions(item);
      case '5': // Live
        return renderLiveActions(item);
      case '6': // Expired
        return renderExpiredActions(item);
      default: // Return empty fragment for any other status code returned
        return renderEmptyStatusActions(item);
    }
  };

  const renderDefaultActions = () => {
    return (
      <div className='flex-column'>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link to={''} onClick={onContactClick}>
            Contact
          </Link>
        </div>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link to={''} onClick={onHelpClick}>
            Help
          </Link>
        </div>
      </div>
    );
  };

  const renderExpiredActions = (item: DealerDashboardItem) => {
    return (
      <div className='flex-column'>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <InsuranceDocumentLink
            quoteId={Number(item.quote)}
            documentType='P0102'
            displayText='Print'
          />
        </div>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <InsuranceDocumentLink
            quoteId={Number(item.quote)}
            documentType='P4202'
            displayText='Print excess voucher'
          />
        </div>
        {renderDefaultActions()}
      </div>
    );
  };

  const renderDeclinedActions = (item: DealerDashboardItem) => {
    return (
      <div className='flex-column'>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link to={''} onClick={() => onClickEdit(item)}>
            Edit
          </Link>
        </div>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link to={''} onClick={() => onClickEdit(item)}>
            Edit Start Date
          </Link>
        </div>
        {item.includeDriveaway && (
          <div className='dealer-link-item'>
            <span className='dealer-link-icon'>{chevronRightComponent}</span>
            <InsuranceDocumentLink
              quoteId={Number(item.quote)}
              documentType='P0102'
              displayText='Print'
            />
          </div>
        )}
        {item.includeExcessReturn && (
          <div className='dealer-link-item'>
            <span className='dealer-link-icon'>{chevronRightComponent}</span>
            <InsuranceDocumentLink
              quoteId={Number(item.quote)}
              documentType='P4202'
              displayText='Print excess voucher'
            />
          </div>
        )}
        {renderDefaultActions()}
      </div>
    );
  };

  const renderEmptyStatusActions = (item: DealerDashboardItem) => {
    return (
      <div className='flex-column'>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link to={''} onClick={() => onClickEdit(item)}>
            Edit
          </Link>
        </div>
        {renderDefaultActions()}
      </div>
    );
  };

  const isQuoteEditable = (quoteStartDate: string) => {
    const today = new Date();
    const startDate = new Date(quoteStartDate);
    return startDate < today;
  };

  const renderLiveActions = (item: DealerDashboardItem) => {
    return (
      <div className='flex-column'>
        {isQuoteEditable(item.startDate) && (
          <div>
            <div className='dealer-link-item'>
              <span className='dealer-link-icon'>{chevronRightComponent}</span>
              <Link to={''} onClick={() => onClickEdit(item)}>
                Edit
              </Link>
            </div>
            <div className='dealer-link-item'>
              <span className='dealer-link-icon'>{chevronRightComponent}</span>
              <Link to={''} onClick={() => onClickEdit(item)}>
                Edit Start Date
              </Link>
            </div>
          </div>
        )}
        {item.includeDriveaway && (
          <div className='dealer-link-item'>
            <span className='dealer-link-icon'>{chevronRightComponent}</span>
            <InsuranceDocumentLink
              quoteId={Number(item.quote)}
              documentType='P0102'
              displayText='Print'
            />
          </div>
        )}
        {item.includeExcessReturn && (
          <div className='dealer-link-item'>
            <span className='dealer-link-icon'>{chevronRightComponent}</span>
            <InsuranceDocumentLink
              quoteId={Number(item.quote)}
              documentType='P4202'
              displayText='Print excess voucher'
            />
          </div>
        )}
        {renderDefaultActions()}
      </div>
    );
  };

  const renderPendingActions = (item: DealerDashboardItem) => {
    return (
      <div className='flex-column'>
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link to={''} onClick={() => onClickEdit(item)}>
            Edit
          </Link>
        </div>
        {renderActivateAction(item)}
        {renderDefaultActions()}
      </div>
    );
  };

  const renderActivateAction = (item: DealerDashboardItem) => {
    if (item.includeDriveaway && item.includeExcessReturn) {
      return (
        <div>
          <div className='dealer-link-item'>
            <span className='dealer-link-icon'>{chevronRightComponent}</span>
            <Link
              to={''}
              onClick={() => {
                onShowQuoteModal(
                  item.includeExcessReturn,
                  item.includeDriveaway,
                  item.quote,
                  item.vehicleRegistrationNumber ?? ''
                );
              }}>
              Activate
            </Link>
          </div>
          <div className='dealer-link-item'>
            <span className='dealer-link-icon'>{chevronRightComponent}</span>
            <InsuranceDocumentLink
              quoteId={Number(item.quote)}
              documentType='P4202'
              displayText='Print excess voucher'
            />
          </div>
        </div>
      );
    } else if (item.includeDriveaway && !item.includeExcessReturn) {
      return (
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link
            to={''}
            onClick={() => {
              onShowQuoteModal(
                item.includeExcessReturn,
                item.includeDriveaway,
                item.quote,
                item.vehicleRegistrationNumber ?? ''
              );
            }}>
            Activate
          </Link>
        </div>
      );
    } else if (!item.includeDriveaway && item.includeExcessReturn) {
      return (
        <div className='dealer-link-item'>
          <span className='dealer-link-icon'>{chevronRightComponent}</span>
          <Link
            to={''}
            onClick={() => {
              onShowQuoteModal(
                item.includeExcessReturn,
                item.includeDriveaway,
                item.quote,
                item.vehicleRegistrationNumber ?? ''
              );
            }}>
            Activate Excess
          </Link>
        </div>
      );
    }
  };

  return (
    <div className='table-flex-layout dealer-dashboard-page'>
      <div className='form-container'>
        <div className='flex-column'>
          <h3>Search records</h3>
          <p>Use the options below to search for a specific record.</p>
        </div>
        <hr className='flex-row' />
        <div className='flex-row'>
          <div className='two-col'>
            <div className='control-label'>Quote number</div>
            <div className='control'>
              <TextInput
                placeholder='Quote number'
                name='quote-number'
                className='input-text'
                value={pageFields.quoteNumber}
                onChange={onChangeQuote}
                error={pageErrors.quoteNumber}
                readOnly={false}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>Customer surname</div>
            <div className='control'>
              <TextInput
                placeholder='Customer surname'
                name='surname'
                className='input-text'
                value={pageFields.customerSurname}
                onChange={onChangeCustomerSurname}
                error={pageErrors.customerSurname}
                readOnly={false}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>Postcode:</div>
            <div className='control'>
              <TextInput
                placeholder='postcode'
                name='postcode'
                className='input-text'
                value={pageFields.postcode}
                onChange={onChangePostcode}
                error={pageErrors.postcode}
                readOnly={false}
              />
            </div>
          </div>
          <div className='two-col'>
            <RadioOptions
              errorMessage={pageErrors.showFinanceApplications}
              selectedOption={pageFields.showFinanceApplications}
              optionsList={DefaultRadioOptions}
              setSelectedOption={onChangeShowFinanceApplications}
              label='Show finance applications'
              name='show-finance-applications'
            />
          </div>
          <div className='two-col'>
            <div className='control-label'>Cover note start date</div>
            <div className='control'>
              <DateInput
                name='cover-note-start-date'
                id='cover-note-start-date'
                className='input-text small'
                forceTimeReset
                error={pageErrors.coverNoteStartDate}
                value={pageFields.coverNoteStartDate}
                onDateChanged={onChangeCoverNoteStartDate}
                maximumDate={new Date()}
                culture={site.cultureCode}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>Cover note end date</div>
            <div className='control'>
              <DateInput
                name='cover-note-end-date'
                id='cover-note-end-date'
                className='input-text small'
                forceTimeReset
                error={pageErrors.coverNoteEndDate}
                value={pageFields.coverNoteEndDate}
                onDateChanged={onChangeCoverNoteEndDate}
                minimumDate={new Date()}
                maximumDate={getMaxCoverNoteEndDate()}
                culture={site.cultureCode}
              />
            </div>
          </div>
        </div>
        <hr className='flex-row' />
        <div className='flex-row'>
          <SelectInput
            name='page-records'
            className='dealer-dashboard-grid-pager'
            value={pageFields.recordsPerPage}
            options={pageRecordOptions}
            onChange={onChangeRecordsPerPage}
            forceDefault
            error={pageErrors.recordsPerPage}
          />
          <span className='dealer-dashboard-pager-text'>records per page</span>
          <div className='dealer-dashboard-search-button'>
            <ButtonContinue
              name='button-search'
              text='Search'
              additionalClassNames='btn-continue'
              onClick={onSearchClick}
            />
          </div>
        </div>
        <div className='dealer-quotes-container'>
          <LoadingContainer loading={loadingData} message='Loading quotes'>
            <div className='dealer-search-results-container'>
              <table className='dealer-search-results'>
                <tr>
                  <th>Quote</th>
                  <th>Customer</th>
                  <th>Dealer</th>
                  <th>Postcode</th>
                  <th>Vehicle Reg</th>
                  <th>Make</th>
                  <th>New/Used</th>
                  <th>Start date</th>
                  <th>Product type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
                {dashboardResults.map((r, i) => (
                  <tr key={`quote-item-${i}`}>
                    <td>{r.quote}</td>
                    <td>{r.customer}</td>
                    <td>{r.dealer}</td>
                    <td>{r.postcode}</td>
                    <td>{r.vehicleRegistrationNumber}</td>
                    <td>{r.make}</td>
                    <td>{r.newOrUsed}</td>
                    <td>{r.startDate}</td>
                    <td>{r.productType}</td>
                    <td>{r.status}</td>
                    <td>{renderActionBlock(r)}</td>
                  </tr>
                ))}
              </table>
            </div>
          </LoadingContainer>
        </div>
      </div>
      <Modal
        role='modal-popup'
        isOpen={showPopup}
        className={`insurance-${site.brand.brandName}`}
        toggle={onCloseModal}>
        <ModalHeader toggle={onCloseModal}>{popupTitle}</ModalHeader>
        <ModalBody>
          <p>{popupMessageParagraph1}</p>
          <p>{popupMessageParagraph2}</p>
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='btn-continue'
            text='Continue'
            onClick={onCloseModal}
            additionalClassNames='btn-ok'
          />
        </ModalFooter>
      </Modal>
      <DealerQuoteDialog
        showDialog={showQuoteDialog}
        onCloseModal={onCloseQuoteModal}
        onContinueModal={onContinueQuoteModal}
        brand={brand.brandName}
        includeExcessReturn={includeExcessReturn}
        includeDriveaway={includeDriveaway}
        quoteId={quoteId}
        vrn={activeVrn}
      />
      <ButtonBack
        name='btnBack'
        text='Back'
        additionalClassNames='btn-back'
        onClick={() => navigate(-1)}
      />
    </div>
  );
};

export default DealerAdminPage;
